import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { DEFAULT_API_BASE, STORAGE_KEYS } from '../config';
import { extractApiErrorMessage } from '../utils/apiErrorUtils';
import {
  clearStoredAuthUser,
  getStoredAuthUser,
  persistStoredAuthUser,
  refreshStoredAuthUser,
} from '../utils/authSessionStorage';
import { isAccessBlockedResponse } from '../utils/authResponseUtils';
import {
  AUTH_REDIRECT_REASONS,
  storeAuthRedirectContext,
} from '../utils/authRedirectContext';
import { useConfig } from './config-provider';

const AuthContext = createContext(null);

const normalizeUser = (input) => {
  if (!input) {
    return null;
  }

  const restaurants = Array.isArray(input.restaurants)
    ? input.restaurants.map((item) => ({
        id: item.id,
        name: item.name || '',
        companyId: item.companyId || input.companyId,
      }))
    : [];

  const restaurantIds =
    Array.isArray(input.restaurantIds) && input.restaurantIds.length > 0
      ? input.restaurantIds
      : restaurants.map((r) => r.id);

  const isAdminMaster = Boolean(input.isAdminMaster);

  const companies =
    Array.isArray(input.companies) && input.companies.length > 0
      ? input.companies.map((company) => ({
          id: company.id,
          name: company.name || '',
        }))
      : isAdminMaster
        ? []
        : [{ id: input.companyId, name: input.companyName || '' }];

  return {
    id: input.id,
    companyId: input.companyId,
    companyName: input.companyName || '',
    name: input.name,
    email: input.email,
    role: input.role,
    restaurantIds,
    restaurants,
    defaultRestaurantId: input.defaultRestaurantId || null,
    defaultRestaurantName: input.defaultRestaurantName || null,
    apiBaseUrl: input.apiBaseUrl || null,
    isAdminMaster,
    companies,
    hasSubscription: Boolean(input.hasSubscription),
    subscriptionAccessAllowed: input.subscriptionAccessAllowed !== false,
    subscriptionStatus: input.subscriptionStatus || null,
    subscriptionBlockingReason: input.subscriptionBlockingReason || null,
    mobileDownloadNoticeAcknowledged: Boolean(input.mobileDownloadNoticeAcknowledged),
  };
};

const assertNormalizedUser = (user) => {
  if (!user) {
    throw new Error('Resposta de autenticação inválida recebida do servidor.');
  }

  return user;
};

const readLocalStorageValue = (key) => {
  if (typeof window === 'undefined' || !key) {
    return null;
  }

  try {
    const value = window.localStorage.getItem(key);
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    return normalized.length ? normalized : null;
  } catch {
    return null;
  }
};

const readPreferredScope = () => {
  const companyId = readLocalStorageValue(STORAGE_KEYS.companyId);

  const restaurantScopedKey = companyId
    ? `${STORAGE_KEYS.restaurantId}:${companyId}`
    : STORAGE_KEYS.restaurantId;
  const restaurantId = readLocalStorageValue(restaurantScopedKey);

  return { companyId, restaurantId };
};

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR');

const pickCompanyForUser = (user, currentCompanyId) => {
  if (!user) {
    return null;
  }

  const linkedCompanies = Array.isArray(user.companies)
    ? user.companies.filter((item) => item?.id)
    : [];

  if (currentCompanyId && linkedCompanies.some((item) => item.id === currentCompanyId)) {
    return currentCompanyId;
  }

  if (linkedCompanies.length) {
    return [...linkedCompanies].sort(byName)[0].id;
  }

  if (currentCompanyId && (user.isAdminMaster || user.role === 'Admin')) {
    return currentCompanyId;
  }

  if (user.companyId) {
    return user.companyId;
  }

  const restaurantCompanyFallback = user.restaurants?.find(
    (item) => item.companyId,
  )?.companyId;
  return restaurantCompanyFallback || null;
};

const pickRestaurantForCompany = (user, companyId, currentRestaurantId) => {
  if (!user) {
    return null;
  }

  const restaurants = Array.isArray(user.restaurants)
    ? user.restaurants.filter((item) => item?.id)
    : [];

  if (!companyId) {
    if (currentRestaurantId && restaurants.some((item) => item.id === currentRestaurantId)) {
      return currentRestaurantId;
    }
    const firstGlobal = [...restaurants].sort(byName)[0];
    return firstGlobal?.id || null;
  }

  const scopedRestaurants = restaurants
    .filter((item) => item.companyId === companyId)
    .sort(byName);

  if (currentRestaurantId && scopedRestaurants.some((item) => item.id === currentRestaurantId)) {
    return currentRestaurantId;
  }

  return scopedRestaurants[0]?.id || null;
};

const isRecoverableBootstrapError = (error) => {
  if (!error?.response) {
    return true;
  }

  const status = error.response.status;
  return status >= 500 || status === 429;
};

const buildSessionIssue = (type, message) => {
  if (type === 'blocked') {
    return {
      type,
      title: 'Acesso temporariamente bloqueado',
      message:
        String(message || '').trim() ||
        'A assinatura da empresa está bloqueada no momento. Regularize a situação comercial para retomar o painel.',
    };
  }

  return {
    type: 'recoverable',
    title: 'Não foi possível validar sua sessão',
    message:
      String(message || '').trim() ||
      'O painel não conseguiu confirmar seu acesso agora. Verifique a conexão e tente novamente.',
  };
};

const shouldBootstrapRouteSession = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pathname = typeof window.location?.pathname === 'string'
    ? window.location.pathname.trim()
    : '';

  if (!pathname) {
    return false;
  }

  return pathname === '/painel' || pathname.startsWith('/painel/');
};

const resolveInitialSessionState = () => {
  const storedSession = getStoredAuthUser();
  const storedUser = normalizeUser(storedSession?.user);
  const hasStoredSession = Boolean(storedUser);
  const shouldBootstrapByRoute = shouldBootstrapRouteSession();

  return {
    storedSession,
    storedUser,
    hasStoredSession,
    shouldBootstrap: hasStoredSession || shouldBootstrapByRoute,
    shouldPersistBootstrap: hasStoredSession,
  };
};

export const AuthProvider = ({ children }) => {
  const {
    apiBase,
    setApiBase,
    setCompanyId,
    setOperationalScope,
    resetRestaurant,
  } = useConfig();

  const initialSessionRef = useRef(null);

  if (initialSessionRef.current === null) {
    initialSessionRef.current = resolveInitialSessionState();
  }

  const [user, setUser] = useState(() => initialSessionRef.current.storedUser);
  const [isLoading, setIsLoading] = useState(() => initialSessionRef.current.shouldBootstrap);
  const [sessionIssue, setSessionIssue] = useState(null);

  const isAuthenticated = useMemo(() => Boolean(user), [user]);

  const applyPreferences = useCallback(
    (normalizedUser, preferredCompanyId = null, preferredRestaurantId = null) => {
      if (!normalizedUser) {
        return;
      }

      const targetCompanyId = pickCompanyForUser(normalizedUser, preferredCompanyId);
      const targetRestaurant = pickRestaurantForCompany(
        normalizedUser,
        targetCompanyId,
        preferredRestaurantId,
      );
      setOperationalScope(targetCompanyId, targetRestaurant);
    },
    [setOperationalScope],
  );

  const clearLocalSession = useCallback(
    ({ reason = null, message = null } = {}) => {
      setUser(null);
      setSessionIssue(null);
      resetRestaurant();
      setCompanyId(null);
      setApiBase(DEFAULT_API_BASE);

      clearStoredAuthUser();

      if (reason) {
        storeAuthRedirectContext({ reason, message });
      }
    },
    [resetRestaurant, setApiBase, setCompanyId],
  );

  const clearLocalSessionRef = useRef(clearLocalSession);
  clearLocalSessionRef.current = clearLocalSession;

  const markSessionExpired = useCallback(
    (message) => {
      clearLocalSession({
        reason: AUTH_REDIRECT_REASONS.sessionExpired,
        message:
          String(message || '').trim() ||
          'Sua sessão expirou. Faça login novamente para continuar.',
      });
    },
    [clearLocalSession],
  );

  const markAccessBlocked = useCallback((message) => {
    setSessionIssue(buildSessionIssue('blocked', message));
  }, []);

  const hydrateAuthenticatedUser = useCallback(async ({ persist = true, signal = null } = {}) => {
    const base = (apiBase || DEFAULT_API_BASE).replace(/\/+$/, '');
    const { data } = await axios.get(`${base}/api/v1/auth/me`, {
      withCredentials: true,
      signal: signal instanceof AbortSignal ? signal : undefined,
    });

    if (signal?.aborted) {
      return null;
    }

    const normalized = assertNormalizedUser(normalizeUser(data));
    setUser(normalized);
    setSessionIssue(null);
    if (persist) {
      refreshStoredAuthUser(normalized);
    }

    const preferred = readPreferredScope();
    applyPreferences(normalized, preferred.companyId, preferred.restaurantId);
    return normalized;
  }, [apiBase, applyPreferences]);

  const shouldPersistHydratedSession = useCallback(() => {
    if (initialSessionRef.current?.shouldPersistBootstrap) {
      return true;
    }

    return Boolean(getStoredAuthUser()?.user);
  }, []);

  const retrySession = useCallback(async () => {
    setIsLoading(true);
    try {
      await hydrateAuthenticatedUser({ persist: shouldPersistHydratedSession() });
    } catch (error) {
      if (isAccessBlockedResponse(error)) {
        setSessionIssue(
          buildSessionIssue(
            'blocked',
            extractApiErrorMessage(
              error,
              'A assinatura da empresa continua bloqueada para uso do painel.',
            ),
          ),
        );
        return;
      }

      if (error?.response?.status === 401) {
        markSessionExpired(
          extractApiErrorMessage(
            error,
            'Sua sessão expirou. Faça login novamente para continuar.',
          ),
        );
        return;
      }

      setSessionIssue(
        buildSessionIssue(
          'recoverable',
          extractApiErrorMessage(
            error,
            'Não foi possível validar sua sessão agora. Tente novamente em instantes.',
          ),
        ),
      );
    } finally {
      setIsLoading(false);
    }
  }, [hydrateAuthenticatedUser, markSessionExpired, shouldPersistHydratedSession]);

  const bootstrapCompletedRef = useRef(false);

  useEffect(() => {
    if (bootstrapCompletedRef.current) {
      return;
    }

    const abortController = new AbortController();

    const loadUser = async () => {
      // A sessão só é reidratada quando o próprio frontend tem um marcador
      // armazenado. Isso evita flicker nas rotas públicas e mantém a opção
      // "Lembrar-me" alinhada ao escopo persistente vs. sessão da aba.
      if (!initialSessionRef.current?.shouldBootstrap) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        await hydrateAuthenticatedUser({
          persist: Boolean(initialSessionRef.current?.shouldPersistBootstrap),
          signal: abortController.signal,
        });

        if (!abortController.signal.aborted) {
          bootstrapCompletedRef.current = true;
        }
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }

        if (isAccessBlockedResponse(error)) {
          setSessionIssue(
            buildSessionIssue(
              'blocked',
              extractApiErrorMessage(
                error,
                'A assinatura da empresa está bloqueada para uso do painel.',
              ),
            ),
          );
          bootstrapCompletedRef.current = true;
          return;
        }

        if (error?.response?.status === 401) {
          clearLocalSessionRef.current({
            reason: AUTH_REDIRECT_REASONS.sessionExpired,
            message: extractApiErrorMessage(
              error,
              'Sua sessão expirou. Faça login novamente para continuar.',
            ),
          });
          bootstrapCompletedRef.current = true;
          return;
        }

        const hadStoredSession = Boolean(getStoredAuthUser()?.user);
        if (hadStoredSession && isRecoverableBootstrapError(error)) {
          setSessionIssue(
            buildSessionIssue(
              'recoverable',
              extractApiErrorMessage(
                error,
                'Não foi possível confirmar sua sessão agora. Verifique a conexão e tente novamente.',
              ),
            ),
          );
          return;
        }

        setUser(null);
        setSessionIssue(null);
        clearStoredAuthUser();
        bootstrapCompletedRef.current = true;
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadUser().catch(() => undefined);

    return () => {
      abortController.abort();
    };
  }, [hydrateAuthenticatedUser]);

  const login = useCallback(
    ({ user: rawUser }, options = {}) => {
      const normalizedUser = assertNormalizedUser(normalizeUser(rawUser));
      const rememberMe = options.rememberMe !== false;
      setUser(normalizedUser);
      setSessionIssue(null);
      persistStoredAuthUser(normalizedUser, { rememberMe });

      applyPreferences(normalizedUser, null, null);
    },
    [applyPreferences],
  );

  const acknowledgeMobileDownloadNotice = useCallback(async () => {
    const base = (apiBase || DEFAULT_API_BASE).replace(/\/+$/, '');
    try {
      await axios.post(
        `${base}/api/v1/user/preferences/mobile-download-notice/acknowledge`,
        {},
        { withCredentials: true },
      );
    } catch (error) {
      if (isAccessBlockedResponse(error)) {
        markAccessBlocked(
          extractApiErrorMessage(
            error,
            'O acesso operacional está bloqueado no momento. Regularize a assinatura para continuar.',
          ),
        );
      } else if (error?.response?.status === 401) {
        markSessionExpired(
          extractApiErrorMessage(
            error,
            'Sua sessão expirou. Faça login novamente para continuar.',
          ),
        );
      }

      throw error;
    }

    if (!user) {
      return;
    }

    const updatedUser = {
      ...user,
      mobileDownloadNoticeAcknowledged: true,
    };

    setUser(updatedUser);
    refreshStoredAuthUser(updatedUser);
  }, [apiBase, markAccessBlocked, markSessionExpired, user]);

  const logout = useCallback(async () => {
    try {
      const base = (apiBase || DEFAULT_API_BASE).replace(/\/+$/, '');
      await axios.post(`${base}/api/v1/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout failed', error);
    }

    clearLocalSession();
  }, [apiBase, clearLocalSession]);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      sessionIssue,
      retrySession,
      markSessionExpired,
      markAccessBlocked,
      acknowledgeMobileDownloadNotice,
      login,
      logout,
    }),
    [
      isAuthenticated,
      isLoading,
      acknowledgeMobileDownloadNotice,
      login,
      logout,
      markAccessBlocked,
      markSessionExpired,
      retrySession,
      sessionIssue,
      user,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

